// Content parsing utilities for different stages

/**
 * Extract revised content from server response
 */
export const extractRevisedContent = (content, stage) => {
  if (!content) {
    return '';
  }

  const hasRevisionMarkers = content.includes('REVISED') || content.includes('UPDATED');
  if (!hasRevisionMarkers) {
    return content;
  }

  const revisionMarkers = {
    story: ['REVISED STORY', 'REVISED CONTENT', 'UPDATED STORY'],
    dialogues: ['REVISED DIALOGUES', 'REVISED CONTENT', 'UPDATED DIALOGUES'],
    questions: ['REVISED QUESTIONS', 'REVISED CONTENT', 'UPDATED QUESTIONS'],
  };

  const markers = revisionMarkers[stage] || ['REVISED', 'UPDATED'];
  let revisedIndex = -1;
  let foundMarker = '';

  if (stage === 'questions') {
    const revisedMatch = content.match(/REVISED QUESTIONS\s*\([^)]*\):\s*/i);
    if (revisedMatch) {
      revisedIndex = content.indexOf(revisedMatch[0]);
      foundMarker = revisedMatch[0];
    }
  }

  if (revisedIndex === -1) {
    for (const marker of markers) {
      const index = content.indexOf(marker);
      if (index !== -1) {
        revisedIndex = index;
        foundMarker = marker;
        break;
      }
    }
  }

  if (revisedIndex === -1) {
    return content;
  }

  let revisedContent;
  if (foundMarker.includes('(') && foundMarker.includes('):')) {
    revisedContent = content.substring(revisedIndex + foundMarker.length);
  } else {
    revisedContent = content.substring(revisedIndex);
  }

  if (stage !== 'story') {
    return revisedContent;
  }

  const sections = ['Summary', 'Learning Outcomes', 'Keywords', 'Concepts'];
  const lines = revisedContent.split('\n');
  const sectionIndices = {};

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    for (const section of sections) {
      const sectionLower = section.toLowerCase();
      const lineLower = line.toLowerCase();
      if (
        lineLower.startsWith(sectionLower) ||
        lineLower.startsWith(`${sectionLower}:`) ||
        lineLower === sectionLower ||
        lineLower === `${sectionLower}:`
      ) {
        if (!sectionIndices[section]) {
          sectionIndices[section] = [];
        }
        sectionIndices[section].push(i);
      }
    }
  }

  for (const section of sections) {
    if (sectionIndices[section] && sectionIndices[section].length > 1) {
      const indicesToRemove = sectionIndices[section].slice(0, -1);
      const lastIndex = sectionIndices[section][sectionIndices[section].length - 1];

      for (const indexToRemove of indicesToRemove) {
        let nextSectionIndex = lines.length;
        for (const otherSection of sections) {
          if (otherSection !== section && sectionIndices[otherSection]) {
            for (const otherIndex of sectionIndices[otherSection]) {
              if (otherIndex > indexToRemove && otherIndex < nextSectionIndex) {
                nextSectionIndex = otherIndex;
              }
            }
          }
        }

        if (lastIndex > indexToRemove && lastIndex < nextSectionIndex) {
          nextSectionIndex = lastIndex;
        }

        for (let i = indexToRemove; i < nextSectionIndex; i++) {
          lines[i] = null;
        }
      }
    }
  }

  return lines.filter((line) => line !== null).join('\n');
};

/**
 * Parse questions content into structured format
 */
export const parseQuestionsContent = (content) => {
  if (!content) {
    return [];
  }

  let cleanContent = content;
  if (content.startsWith('&') && content.endsWith('&')) {
    cleanContent = content.slice(1, -1);
  }

  let contentToParse = cleanContent;
  const revisedMatch = cleanContent.match(/REVISED QUESTIONS\s*\([^)]*\):\s*/i);
  if (revisedMatch) {
    const revisedIndex = cleanContent.indexOf(revisedMatch[0]);
    contentToParse = cleanContent.substring(revisedIndex + revisedMatch[0].length);
  } else {
    const revisedIndex = cleanContent.indexOf('REVISED QUESTIONS');
    if (revisedIndex !== -1) {
      contentToParse = cleanContent.substring(revisedIndex);
    }
  }

  const questions = [];
  let questionMatches = contentToParse.match(/Question\s+(\d+)[\s\S]*?(?=Question\s+\d+|$)/gi);

  if (!questionMatches || questionMatches.length === 0) {
    questionMatches = contentToParse.match(/Q\d+:[\s\S]*?(?=Q\d+:|$)/gi);
  }

  if (!questionMatches || questionMatches.length === 0) {
    questionMatches = contentToParse.split('\n\n').filter((q) => q.trim() !== '');
  }

  if (!questionMatches || questionMatches.length === 0) {
    return questions;
  }

  questionMatches.forEach((questionBlock, index) => {
    let questionText = '';
    let options = [];
    let correctAnswer = '';

    const questionMatch = questionBlock.match(/(?:Question\s+\d+:|Q\d+:)\s*(.+?)(?=\n|$)/i);
    if (questionMatch) {
      questionText = questionMatch[1].trim();
    }

    let optionMatches = questionBlock.match(/[A-D]\)\s*[^\n]+/g);
    if (optionMatches && optionMatches.length >= 4) {
      options = optionMatches.map((option) => option.replace(/^[A-D]\)\s*/, '').trim());
    } else {
      optionMatches = questionBlock.match(/[A-D]\.\s*[^\n]+/g);
      if (optionMatches && optionMatches.length >= 4) {
        options = optionMatches.map((option) => option.replace(/^[A-D]\.\s*/, '').trim());
      } else {
        optionMatches = questionBlock.match(/(?:[A-D][\)\.])\s*[^\n]+/g);
        if (optionMatches) {
          options = optionMatches.map((option) => option.replace(/^[A-D][\)\.]\s*/, '').trim());
        }
      }
    }

    if (options.length < 4) {
      const allOptionLines = questionBlock.split('\n').filter((line) => {
        const trimmed = line.trim();
        return /^[A-D][\)\.]\s*/.test(trimmed);
      });

      if (allOptionLines.length > options.length) {
        options = allOptionLines.map((line) => line.replace(/^[A-D][\)\.]\s*/, '').trim());
      }
    }

    const answerMatch = questionBlock.match(/(?:Answer|Correct Answer):\s*([A-D])/i);
    if (answerMatch) {
      correctAnswer = answerMatch[1];
    }

    let explanation = '';
    const explanationMatch = questionBlock.match(/(?:Explanation|Why|Reason):\s*(.+?)(?=\n(?:Question|Q\d+)|$)/is);
    if (explanationMatch) {
      explanation = explanationMatch[1].trim();
    } else {
      explanation = `The correct answer is ${correctAnswer}. This option best addresses the question asked.`;
    }

    if (questionText) {
      questions.push({
        id: index + 1,
        question: questionText,
        options,
        correctAnswer,
        explanation,
        rawText: questionBlock.trim(),
      });
    }
  });

  return questions;
};
